import React, { useEffect, useState } from 'react';
import { Menu, X, Phone } from 'lucide-react';

const navLinks = [
  { label: 'Leistungen', sectionId: 'services' },
  { label: 'Über uns', sectionId: 'about' },
  { label: 'Referenzen', sectionId: 'projects' },
  { label: 'Kontakt', sectionId: 'contact' },
];

const Header = ({ scrollToSection }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bgActive, setBgActive] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll('[data-bg]');

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.find((e) => e.isIntersecting);
        if (!entry) {
          setBgActive(false);
          return;
        }

        const bg = entry.target.getAttribute('data-bg');
        setBgActive(bg === 'light');
      },
      {
        root: null,
        rootMargin: '-90px 0px -90% 0px',
        threshold: 0,
      }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const navActive = scrolled || bgActive || mobileOpen;

  const handleNavClick = (sectionId) => {
    scrollToSection?.(sectionId);
    setMobileOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div
        className={[
          'transition-all duration-500 ease-out',
          navActive
            ? 'bg-eos-prim-blue/95 backdrop-blur-md shadow-lg py-3'
            : 'bg-transparent py-6',
        ].join(' ')}
      >
        <div className="w-full flex items-center justify-between px-4 sm:px-6 lg:px-12 xl:px-16">
          <button
            type="button"
            onClick={() => handleNavClick('hero')}
            className="flex items-center"
            aria-label="Startseite"
          >
            <div className="h-12 w-40 sm:h-14 sm:w-44 bg-logo_weis bg-contain bg-no-repeat bg-left" />
          </button>

          <div className="hidden md:flex flex-1 items-center justify-end gap-8 ml-8">
            {navLinks.map((link) => (
              <button
                key={link.sectionId}
                type="button"
                onClick={() => handleNavClick(link.sectionId)}
                className="text-eos-weißFast/80 hover:text-eos-weißFast text-sm font-medium tracking-wide transition-colors duration-300 relative after:content-[''] after:absolute after:bottom-[-6px] after:left-0 after:w-0 after:h-0.5 after:bg-eos-weißFast after:transition-all after:duration-300 hover:after:w-full"
              >
                {link.label}
              </button>
            ))}

            <a
              href="tel:+4915168581912"
              className="flex items-center gap-2 bg-eos-weißFast hover:bg-eos-weißFaster text-eos-grauDunkel px-5 py-2.5 rounded-md text-sm font-medium transition-all duration-300"
            >
              <Phone className="w-4 h-4" />
              Anrufen
            </a>
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="md:hidden text-eos-weißFast p-2"
            aria-label={mobileOpen ? 'Menü schließen' : 'Menü öffnen'}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-eos-prim-blue/98 backdrop-blur-md border-t border-white/10">
          <div className="mx-auto max-w-7xl flex flex-col px-4 sm:px-6 py-5 gap-4">
            {navLinks.map((link) => (
              <button
                key={link.sectionId}
                type="button"
                onClick={() => handleNavClick(link.sectionId)}
                className="text-left text-eos-weißFast/85 hover:text-eos-weißFast text-base font-medium py-2 transition-colors"
              >
                {link.label}
              </button>
            ))}

            <a
              href="tel:+4915168581912"
              className="flex items-center justify-center gap-2 bg-eos-weißFast text-eos-grauDunkel px-5 py-3 rounded-md font-medium mt-2"
            >
              <Phone className="w-4 h-4" />
              +49 151 6858 1912
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
